'use client';

import { useTranslations } from 'next-intl';
import {
  useRef,
  useState,
  useLayoutEffect,
  type JSX,
  type ComponentProps,
} from 'react';

import type { VariantProps } from 'class-variance-authority';

import { cn } from '~/utils';
import { Show } from '~/components/show';
import { Stack } from '~/components/stack';
import { Button } from '~/components/button';
import { Text, type textVariants } from '~/components/text';

interface ITruncatedTextProps
  extends ComponentProps<'p'>,
    VariantProps<typeof textVariants> {
  height?: number;
}

export function TruncatedText({
  style,
  className,
  height = 84,
  ...props
}: ITruncatedTextProps): JSX.Element {
  const t = useTranslations();
  const textRef = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [showButton, setShowButton] = useState<boolean>(false);

  useLayoutEffect(() => {
    if (!textRef.current) {
      return;
    }

    if (textRef.current.scrollHeight > height) {
      textRef.current.style.height = `${height}px`;
      setShowButton(true);
    }
  }, [height]);

  const handleClickShowMore = () => {
    if (!textRef.current) {
      return;
    }

    if (expanded) {
      textRef.current.style.height = `${height}px`;
    } else {
      textRef.current.style.height = `${textRef.current.scrollHeight}px`;
    }

    setExpanded((state) => !state);
  };

  return (
    <Stack gap={2}>
      <Text
        ref={textRef}
        style={style}
        className={cn('overflow-hidden transition-all', className)}
        {...props}
      />
      <Show when={showButton}>
        <Button
          variant='link'
          onClick={handleClickShowMore}
          className='self-start p-0 underline h-[unset]'
        >
          {t(`commons.see${expanded ? 'Less' : 'More'}`)}
        </Button>
      </Show>
    </Stack>
  );
}
